import { type RefObject, useEffect, useState } from 'react'

/** One beat of the loop. The order here is the order they play. */
export enum Scene {
  /** Six closed cards, at rest. */
  ROW = 'row',
  /** The two centre cards lift, telegraphing the click that follows. */
  ARMED = 'armed',
  /** The anchor card is open; the other five sit in the compare rail. */
  SINGLE = 'single',
  /** A second card opens beside it, and the pointer zig-zags down between the two. */
  COMPARE = 'compare',
  /** The pointer leaves its zig-zag and travels to the anchor card's Add to cart. */
  POINTING = 'pointing',
  /** The click lands and the card flips to its added state. */
  ADDED = 'added',
  /** Both open cards fade back down to the row. */
  CLOSING = 'closing',
}

const TIMELINE: { scene: Scene; ms: number }[] = [
  { scene: Scene.ROW, ms: 1500 },
  { scene: Scene.ARMED, ms: 700 },
  { scene: Scene.SINGLE, ms: 2100 },
  { scene: Scene.COMPARE, ms: 2400 },
  { scene: Scene.POINTING, ms: 800 },
  { scene: Scene.ADDED, ms: 1000 },
  { scene: Scene.CLOSING, ms: 450 },
]

const OPEN_SCENES = new Set([Scene.SINGLE, Scene.COMPARE, Scene.POINTING, Scene.ADDED])
const TWO_CARD_SCENES = new Set([Scene.COMPARE, Scene.POINTING, Scene.ADDED, Scene.CLOSING])
const CURSOR_SCENES = new Set([Scene.COMPARE, Scene.POINTING, Scene.ADDED])
const IN_CART_SCENES = new Set([Scene.ADDED, Scene.CLOSING])

export interface QuoteAnimationState {
  scene: Scene
  /** The comparison viewer is on screen; the row is faded out behind it. */
  isOpen: boolean
  /** The centre cards are lifted into their hover state. */
  isArmed: boolean
  /** The second card is mounted — it stays through CLOSING so it fades rather than pops. */
  showsSecondCard: boolean
  showsCursor: boolean
  /** The cursor is still comparing the two open quotes, not yet aiming at the button. */
  isTouring: boolean
  /** The cursor has travelled and is resting on the button. */
  isPressed: boolean
  /** Drives the anchor card's real "Added to cart" state via its cart keys. */
  isInCart: boolean
  /** No motion: the loop is parked on the comparison frame. */
  isStatic: boolean
}

export function prefersReducedMotion() {
  // jsdom and some embedded contexts define `window` but not `matchMedia`.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Plays the loop only while the block is on screen, and parks it on the comparison
 * frame under reduced motion — a marketing page shouldn't run a timer no one is watching.
 */
export function useQuoteAnimation(ref: RefObject<HTMLElement | null>): QuoteAnimationState {
  const reduced = prefersReducedMotion()
  const [active, setActive] = useState(false)
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const el = ref.current
    // jsdom and some embedded contexts define `window` but not IntersectionObserver.
    if (!el || reduced || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setActive(entry.isIntersecting)
        // Restart from the top rather than resuming mid-beat when it scrolls back.
        if (!entry.isIntersecting) setIndex(0)
      },
      { threshold: 0.25 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref, reduced])

  useEffect(() => {
    if (!active || reduced) return
    const timer = window.setTimeout(
      () => setIndex((current) => (current + 1) % TIMELINE.length),
      TIMELINE[index].ms
    )
    return () => clearTimeout(timer)
  }, [index, active, reduced])

  const scene = reduced ? Scene.COMPARE : TIMELINE[index].scene

  return {
    scene,
    isOpen: OPEN_SCENES.has(scene),
    isArmed: scene === Scene.ARMED,
    showsSecondCard: TWO_CARD_SCENES.has(scene),
    showsCursor: !reduced && CURSOR_SCENES.has(scene),
    isTouring: scene === Scene.COMPARE,
    isPressed: scene === Scene.ADDED,
    isInCart: !reduced && IN_CART_SCENES.has(scene),
    isStatic: reduced,
  }
}
